/**
 * Stage 7 — GATE 3, the electronic signature.
 *
 * Signing means: the approver states the meaning of their signature, then
 * re-authenticates (Entra popup with `prompt: "login"` — MFA under Conditional
 * Access), and the backend binds signer, meaning, timestamp and the report's
 * hash into a tamper-evident signature record (21 CFR Part 11 §11.50 / §11.70).
 */

import { useState } from "react";
import { Alert, Badge, Button, Code, Group, Paper, Stack, Text, Textarea } from "@mantine/core";
import {
  IconDownload,
  IconInfoCircle,
  IconLock,
  IconRefresh,
  IconSignature,
  IconX,
} from "@tabler/icons-react";
import { useAccessToken } from "@/features/auth/useAccessToken";
import {
  approveRun,
  downloadArtefact,
  rejectRun,
  reopenRun,
  type RunOut,
} from "@/shared/api/validation";
import { ApiError } from "@/shared/api/apiFetch";
import { promptForReason } from "@/shared/ui/promptForReason";
import { toastApiError, toastSuccess } from "@/shared/ui/toast";
import { READINESS_COLOR, READINESS_LABEL, fmtDate } from "../stageMeta";
import { useSignedAction } from "../useSignedAction";

interface Props {
  run: RunOut;
  onUpdated: (run: RunOut, jump?: boolean) => void;
}

export function ApprovalStage({ run, onUpdated }: Props) {
  const getToken = useAccessToken();
  const getSigningToken = useSignedAction();
  const [meaning, setMeaning] = useState(
    `I approve ${run.change_ref ?? run.title} for Production on Vault ${run.vault_product}`,
  );
  const [busy, setBusy] = useState<"sign" | "reject" | "reopen" | null>(null);

  const sign = async () => {
    setBusy("sign");
    try {
      // First attempt with the current token: if the session is already fresh
      // (auth_time within the window) no popup is needed.
      let token = await getToken();
      try {
        onUpdated(await approveRun(token, run.run_id, meaning.trim()));
      } catch (err) {
        if (err instanceof ApiError && err.code === "signature_reauth_required") {
          token = await getSigningToken();
          onUpdated(await approveRun(token, run.run_id, meaning.trim()));
        } else {
          throw err;
        }
      }
      toastSuccess("Signed", "The validation package is approved and locked.");
    } catch (err) {
      toastApiError("Could not sign", err);
    } finally {
      setBusy(null);
    }
  };

  const reject = async () => {
    const reason = await promptForReason("Reject the release", "Reason", {
      confirmLabel: "Reject",
    });
    if (!reason) return;
    setBusy("reject");
    try {
      onUpdated(await rejectRun(await getToken(), run.run_id, reason));
      toastSuccess("Rejected", "The run can be reopened for re-testing.");
    } catch (err) {
      toastApiError("Could not reject", err);
    } finally {
      setBusy(null);
    }
  };

  const downloadSigned = async (fmt: "md" | "pdf") => {
    try {
      const blob = await downloadArtefact(await getToken(), run.run_id, "report", fmt);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${run.title.slice(0, 40)}-report-signed.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toastApiError("Could not download the signed report", err);
    }
  };

  const reopen = async () => {
    const reason = await promptForReason("Reopen for re-testing", "Reason", {
      confirmLabel: "Reopen",
    });
    if (!reason) return;
    setBusy("reopen");
    try {
      onUpdated(await reopenRun(await getToken(), run.run_id, reason));
      toastSuccess("Reopened", "Failed and blocked tests are back with their testers.");
    } catch (err) {
      toastApiError("Could not reopen", err);
    } finally {
      setBusy(null);
    }
  };

  if (run.status === "approved") {
    return (
      <Paper withBorder radius="md" p="lg">
        <Stack gap="sm">
          <Group gap="sm">
            <IconLock size={20} />
            <Text fw={600}>Approved and locked</Text>
            {run.readiness && (
              <Badge variant="filled" color={READINESS_COLOR[run.readiness]}>
                {READINESS_LABEL[run.readiness]}
              </Badge>
            )}
          </Group>
          <Text size="sm">
            Signed by <b>{run.approved_by_display_name ?? run.approved_by_user_id}</b> on{" "}
            {fmtDate(run.approved_at_utc)}.
          </Text>
          <Text size="xs" c="dimmed">
            Signature record <Code>{run.signature_id}</Code> — binds signer, meaning, server
            timestamp, and the report hash. Every artefact of this run is now read-only.
          </Text>
          <Group gap="xs">
            <Button
              variant="light"
              leftSection={<IconDownload size={16} />}
              onClick={() => void downloadSigned("pdf")}
            >
              Download signed report (PDF)
            </Button>
            <Button variant="subtle" onClick={() => void downloadSigned("md")}>
              Markdown
            </Button>
          </Group>
          <Text size="xs" c="dimmed">
            A certified copy: the report plus the signature block (signer, meaning, timestamps,
            signature record, and the report's SHA-256 as signed) — verifiable against the
            platform's record.
          </Text>
        </Stack>
      </Paper>
    );
  }

  if (run.status === "rejected") {
    return (
      <Paper withBorder radius="md" p="lg">
        <Stack gap="sm">
          <Text fw={600} c="red">
            Rejected
          </Text>
          <Text size="sm">{run.rejection_reason}</Text>
          {run.is_owner && (
            <Button
              w="fit-content"
              variant="light"
              leftSection={<IconRefresh size={16} />}
              loading={busy === "reopen"}
              onClick={() => void reopen()}
            >
              Reopen for re-testing
            </Button>
          )}
        </Stack>
      </Paper>
    );
  }

  if (run.status !== "reported") {
    return (
      <Alert variant="light" color="blue" icon={<IconInfoCircle size={18} />}>
        Sign-off becomes available once the validation report exists (Execution → Evaluate).
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      <Paper withBorder radius="md" p="lg">
        <Stack gap="md">
          <Group gap="sm">
            <Text fw={600}>GxP sign-off</Text>
            {run.readiness && (
              <Badge variant="filled" color={READINESS_COLOR[run.readiness]}>
                Recommendation: {READINESS_LABEL[run.readiness]}
              </Badge>
            )}
          </Group>
          <Text size="sm" c="dimmed">
            Signing re-authenticates you (a second credential — Entra sign-in, with MFA where
            Conditional Access requires it) and records your stated meaning with the report's hash.
            This is the human decision; the recommendation above is advisory.
          </Text>
          <Textarea
            label="Meaning of signature"
            description="What you are attesting to, in your words (21 CFR Part 11 §11.50)."
            autosize
            minRows={2}
            value={meaning}
            onChange={(e) => setMeaning(e.currentTarget.value)}
          />
          <Group justify="space-between">
            <Button
              variant="subtle"
              color="red"
              leftSection={<IconX size={16} />}
              loading={busy === "reject"}
              onClick={() => void reject()}
            >
              Reject
            </Button>
            <Button
              leftSection={<IconSignature size={16} />}
              disabled={meaning.trim().length < 3}
              loading={busy === "sign"}
              onClick={() => void sign()}
            >
              Re-authenticate and sign
            </Button>
          </Group>
        </Stack>
      </Paper>
    </Stack>
  );
}
